/**
 * Demos pédagogiques interactives — Code Voiture
 * 8 démos calibrées par des moniteurs ECSR pour faire comprendre
 * en 30 secondes ce qu'un cours magistral met 30 minutes à expliquer.
 *
 * Usage hub  : <DemoCard slug="mariotte" />
 * Usage embed: <DemoEmbed slug="mariotte" />  (dans un article)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Eye, Car, Gauge, Timer, Zap, Play, RotateCcw, Info, Octagon, Coffee, Wine } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// 1. TACHE AVEUGLE DE MARIOTTE (eye blind spot)
// ─────────────────────────────────────────────────────────────────
export function MariotteDemo() {
  const [side, setSide] = useState('right'); // which eye to close
  const isRight = side === 'right';
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-mariotte">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Eye size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Découvre ta tache aveugle</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Chaque œil a une zone sans aucune cellule visuelle, là où le nerf optique sort de la rétine. Voici comment la faire apparaître :</p>
        </div>
      </div>

      {/* Stage */}
      <div className="relative w-full bg-white rounded-xl overflow-hidden" style={{ aspectRatio: '4 / 1' }}>
        <div className="absolute inset-0 flex items-center justify-between px-[12%]">
          {/* Cross on the left, dot on the right (for closed-left-eye) OR mirrored */}
          {isRight ? (
            <>
              <div className="text-black font-mono-spec text-[42px] sm:text-[64px] leading-none select-none">+</div>
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-600" />
            </>
          ) : (
            <>
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-600" />
              <div className="text-black font-mono-spec text-[42px] sm:text-[64px] leading-none select-none">+</div>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <ol className="text-white/80 text-[13px] sm:text-[14px] space-y-1.5 list-decimal list-inside">
          <li>Place ton visage à <strong className="text-white">~40 cm</strong> de l'écran.</li>
          <li>Ferme l'œil <strong className="text-white">{isRight ? 'gauche' : 'droit'}</strong>.</li>
          <li>Fixe la croix avec l'œil <strong className="text-white">{isRight ? 'droit' : 'gauche'}</strong>.</li>
          <li>Avance/recule lentement → <strong className="text-red-400">le point rouge disparaît !</strong></li>
        </ol>
        <button
          onClick={() => setSide(isRight ? 'left' : 'right')}
          data-testid="mariotte-flip"
          className="h-10 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold inline-flex items-center gap-2"
        >
          <RotateCcw size={14} /> Tester l'autre œil
        </button>
      </div>
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Au volant : ta tache aveugle peut masquer un cycliste ou un piéton entier à 20 m. C'est pour ça qu'on bouge la tête.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2. ANGLE MORT VÉHICULE — top-down SVG view with mirror cones
// ─────────────────────────────────────────────────────────────────
export function VehicleBlindSpotDemo() {
  const stageRef = useRef(null);
  const [pos, setPos] = useState({ x: 0.78, y: 0.55 }); // normalized 0..1
  const [drag, setDrag] = useState(false);

  // Stage = 4:3. Car centered, oriented front=top.
  // Coords in 0..1 normalized space.
  const CAR = { cx: 0.5, cy: 0.5, w: 0.16, h: 0.30 };

  // Blind zones = regions NOT covered by rétroviseurs (3 zones typiques).
  // Définies en polygones normalisés.
  const blindZones = [
    {
      label: 'Angle mort arrière-droit (« 3 heures »)',
      poly: [[0.58, 0.42], [0.88, 0.40], [0.95, 0.78], [0.58, 0.75]],
    },
    {
      label: 'Angle mort arrière-gauche (« 9 heures »)',
      poly: [[0.42, 0.42], [0.12, 0.40], [0.05, 0.78], [0.42, 0.75]],
    },
    {
      label: 'Zone basse avant (capot)',
      poly: [[0.40, 0.20], [0.60, 0.20], [0.62, 0.32], [0.38, 0.32]],
    },
  ];

  // Point-in-polygon
  const pip = (x, y, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, yi] = poly[i], [xj, yj] = poly[j];
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };
  const inZone = blindZones.find((z) => pip(pos.x, pos.y, z.poly));
  // Si dans la voiture → ignore
  const insideCar =
    Math.abs(pos.x - CAR.cx) < CAR.w / 2 &&
    Math.abs(pos.y - CAR.cy) < CAR.h / 2;

  const handleMove = (clientX, clientY) => {
    if (!stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    setPos({
      x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
    });
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-vehicle-blindspot">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Car size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Trouve les angles morts</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Déplace le cycliste autour de la voiture (vue du dessus). Les zones <strong className="text-red-400">rouges</strong> ne sont couvertes par <strong className="text-white">aucun rétroviseur</strong>.</p>
        </div>
      </div>

      <div
        ref={stageRef}
        onMouseMove={(e) => drag && handleMove(e.clientX, e.clientY)}
        onMouseUp={() => setDrag(false)}
        onMouseLeave={() => setDrag(false)}
        onTouchMove={(e) => { const t = e.touches[0]; handleMove(t.clientX, t.clientY); }}
        className="relative w-full bg-gradient-to-b from-[#222226] via-[#161618] to-[#0a0a0b] rounded-xl overflow-hidden touch-none select-none cursor-crosshair"
        style={{ aspectRatio: '4 / 3' }}
      >
        {/* SVG layer for car + zones + mirror cones */}
        <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          {/* Road dashed center line (vertical, behind car) */}
          <line x1="200" y1="0" x2="200" y2="300" stroke="rgba(255,255,255,0.07)" strokeWidth="2" strokeDasharray="6 10" />

          {/* Mirror visibility cones (translucent green) */}
          {/* Cône rétroviseur intérieur — arrière central, étroit */}
          <polygon points="200,150 175,300 225,300" fill="rgba(74,222,128,0.10)" stroke="rgba(74,222,128,0.30)" strokeWidth="0.8" />
          {/* Cône rétro gauche — arrière-gauche partiel */}
          <polygon points="168,165 50,300 168,300" fill="rgba(74,222,128,0.08)" stroke="rgba(74,222,128,0.25)" strokeWidth="0.8" />
          {/* Cône rétro droit — arrière-droit partiel */}
          <polygon points="232,165 350,300 232,300" fill="rgba(74,222,128,0.08)" stroke="rgba(74,222,128,0.25)" strokeWidth="0.8" />
          {/* Cône vision avant (pare-brise) — large */}
          <polygon points="200,135 40,0 360,0" fill="rgba(74,222,128,0.05)" stroke="rgba(74,222,128,0.18)" strokeWidth="0.5" />

          {/* Blind zones overlays (red translucent) */}
          {blindZones.map((z, i) => (
            <polygon
              key={i}
              points={z.poly.map(([x, y]) => `${x * 400},${y * 300}`).join(' ')}
              fill="rgba(220,38,38,0.20)"
              stroke="rgba(220,38,38,0.55)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
          ))}

          {/* Top-down car body */}
          <g transform="translate(200 150)">
            {/* Shadow */}
            <ellipse cx="0" cy="2" rx="36" ry="62" fill="rgba(0,0,0,0.55)" />
            {/* Main body */}
            <rect x="-32" y="-55" width="64" height="110" rx="14" ry="14" fill="#c81f1f" stroke="#7a0f0f" strokeWidth="1.5" />
            {/* Hood (front, upper) */}
            <rect x="-26" y="-50" width="52" height="20" rx="3" fill="#a01818" />
            {/* Trunk (rear, lower) */}
            <rect x="-26" y="32" width="52" height="18" rx="3" fill="#a01818" />
            {/* Windshield (front) */}
            <path d="M -22 -28 L 22 -28 L 18 -8 L -18 -8 Z" fill="#1c2640" opacity="0.85" />
            {/* Rear window */}
            <path d="M -18 10 L 18 10 L 22 30 L -22 30 Z" fill="#1c2640" opacity="0.85" />
            {/* Roof line */}
            <line x1="-22" y1="-8" x2="22" y2="-8" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            <line x1="-22" y1="10" x2="22" y2="10" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            {/* Side mirrors */}
            <rect x="-38" y="-22" width="6" height="5" rx="1" fill="#7a0f0f" />
            <rect x="32" y="-22" width="6" height="5" rx="1" fill="#7a0f0f" />
            {/* Headlights (front) */}
            <circle cx="-18" cy="-50" r="3" fill="#fff8c5" stroke="#ddc100" strokeWidth="0.5" />
            <circle cx="18" cy="-50" r="3" fill="#fff8c5" stroke="#ddc100" strokeWidth="0.5" />
            {/* Tail lights (rear) */}
            <rect x="-22" y="48" width="8" height="4" rx="1" fill="#d93030" />
            <rect x="14" y="48" width="8" height="4" rx="1" fill="#d93030" />
            {/* "AVANT" label */}
            <text x="0" y="-58" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="8" fontFamily="monospace" letterSpacing="1.5">AVANT</text>
            <text x="0" y="62" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="monospace" letterSpacing="1.5">ARRIÈRE</text>
          </g>

          {/* Compass labels */}
          <text x="8" y="160" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace" letterSpacing="1.5">GAUCHE</text>
          <text x="350" y="160" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace" letterSpacing="1.5">DROITE</text>
        </svg>

        {/* Cyclist (HTML over SVG, draggable) */}
        <button
          type="button"
          data-testid="vehicle-blindspot-cyclist"
          onMouseDown={() => setDrag(true)}
          onTouchStart={() => setDrag(true)}
          className="absolute -translate-x-1/2 -translate-y-1/2 text-[28px] sm:text-[36px] cursor-grab active:cursor-grabbing select-none drop-shadow-lg"
          style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%` }}
          aria-label="Déplacer le cycliste"
        >
          🚴
        </button>

        {/* Legend overlay */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-1 text-[10px] sm:text-[11px] font-mono-spec">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-black/60 backdrop-blur border border-emerald-700/40 text-emerald-300">
            <span className="w-2 h-2 rounded-sm bg-emerald-500/40 border border-emerald-400/60" /> Visible par rétro
          </div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-black/60 backdrop-blur border border-red-700/40 text-red-300">
            <span className="w-2 h-2 rounded-sm bg-red-500/40 border border-red-400/60" /> Angle mort
          </div>
        </div>
      </div>

      <div className={`mt-4 rounded-xl p-3 sm:p-4 text-[13px] sm:text-[13.5px] transition-colors ${
        insideCar ? 'bg-white/[0.04] border border-white/10 text-white/60' :
        inZone ? 'bg-red-950/40 border border-red-700/40 text-red-200' :
        'bg-emerald-950/30 border border-emerald-700/30 text-emerald-300'
      }`}>
        {insideCar ? (
          <><strong>⚙ Position invalide.</strong> Tu as placé le cycliste sur la voiture — déplace-le sur la route.</>
        ) : inZone ? (
          <><strong>⚠ Angle mort !</strong> {inZone.label} — Aucun rétroviseur ne couvre cette zone. <strong>Tourne la tête</strong> par-dessus l'épaule avant de te déporter.</>
        ) : (
          <><strong>✓ Visible.</strong> Cette zone est couverte par au moins un rétroviseur (intérieur, gauche, ou droit). Mais attention : à grande vitesse, même un usager visible peut être ignoré si tu ne regardes pas activement.</>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 3. TUNNEL VISUEL SELON LA VITESSE
// ─────────────────────────────────────────────────────────────────
export function VisualTunnelDemo() {
  const [speed, setSpeed] = useState(50);
  // Angle of peripheral vision narrows from ~180° at rest → ~30° at 130 km/h
  const angle = Math.max(30, 180 - speed * 1.15);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-visual-tunnel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Gauge size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">L'effet tunnel</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Plus tu accélères, plus ton champ visuel se rétrécit. À 130 km/h, tu ne vois quasiment que ce qui est <strong className="text-white">droit devant toi</strong>.</p>
        </div>
      </div>

      <div className="relative w-full bg-gradient-to-b from-[#1a1a1c] to-[#0a0a0b] rounded-xl overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
        {/* Driver POV — fake road */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,_#444_0%,_#222_30%,_#0a0a0b_70%)]" />
        {/* Trees / signs in peripheral */}
        {[0.08, 0.18, 0.82, 0.92].map((x, i) => (
          <div key={i} className="absolute text-[28px] sm:text-[36px] select-none" style={{ left: `${x * 100}%`, top: '60%', transform: 'translate(-50%, -50%)' }} aria-hidden="true">🌲</div>
        ))}
        {[0.30, 0.70].map((x, i) => (
          <div key={i} className="absolute text-[22px] sm:text-[26px] select-none" style={{ left: `${x * 100}%`, top: '50%', transform: 'translate(-50%, -50%)' }} aria-hidden="true">🚸</div>
        ))}
        {/* Black tunnel mask = peripheral vision blocked */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse ${angle}% ${angle * 0.6}% at 50% 55%, transparent 0%, transparent 35%, rgba(0,0,0,0.97) 70%, #000 100%)`,
          }}
        />
        {/* Speed display */}
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-md bg-black/60 backdrop-blur border border-white/15 text-white font-mono-spec text-[13px] sm:text-[15px]">
          {speed} km/h · champ visuel : <span className="text-red-400 font-bold">{Math.round(angle)}°</span>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Vitesse</label>
        <input
          data-testid="tunnel-speed-slider"
          type="range" min="30" max="130" value={speed} step="5"
          onChange={(e) => setSpeed(parseInt(e.target.value))}
          className="w-full mt-2 accent-red-500 h-2"
        />
        <div className="flex justify-between text-[11px] text-white/40 font-mono-spec mt-1">
          <span>30</span><span>50</span><span>70</span><span>90</span><span>110</span><span>130</span>
        </div>
      </div>
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Source : ophtalmologie routière — perte ~1,5° par km/h au-delà de 30 km/h.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 4. TEST DE TEMPS DE RÉACTION
// ─────────────────────────────────────────────────────────────────
export function ReactionTimeDemo() {
  const [state, setState] = useState('idle'); // idle | waiting | ready | done | tooSoon
  const [ms, setMs] = useState(null);
  const startRef = useRef(0);
  const timeoutRef = useRef(null);

  const start = () => {
    setState('waiting');
    setMs(null);
    const delay = 1200 + Math.random() * 3500;
    timeoutRef.current = setTimeout(() => {
      startRef.current = performance.now();
      setState('ready');
    }, delay);
  };

  const click = () => {
    if (state === 'waiting') {
      clearTimeout(timeoutRef.current);
      setState('tooSoon');
    } else if (state === 'ready') {
      const elapsed = Math.round(performance.now() - startRef.current);
      setMs(elapsed);
      setState('done');
    } else {
      start();
    }
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Distance at 90 km/h = 25 m/s → distance = ms * 25 / 1000
  const distance90 = ms ? (ms * 25 / 1000).toFixed(1) : null;
  const distance50 = ms ? (ms * 13.9 / 1000).toFixed(1) : null;

  const bgColor =
    state === 'waiting' ? 'bg-red-950 hover:bg-red-900' :
    state === 'ready' ? 'bg-green-500 hover:bg-green-400' :
    state === 'tooSoon' ? 'bg-orange-700' :
    state === 'done' ? 'bg-[#141416] hover:bg-[#1a1a1c]' :
    'bg-[#141416] hover:bg-[#1a1a1c]';

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-reaction-time">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Timer size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Teste ton temps de réaction</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Clique dès que la zone passe au <strong className="text-green-400">VERT</strong>. Pas avant — sinon c'est un faux départ.</p>
        </div>
      </div>

      <button
        type="button"
        data-testid="reaction-zone"
        onClick={click}
        className={`relative w-full rounded-xl flex flex-col items-center justify-center select-none transition-colors ${bgColor} border border-white/10`}
        style={{ aspectRatio: '16 / 8' }}
      >
        {state === 'idle' && <><Play size={36} className="text-white/60 mb-2" /><span className="text-white/85 text-[15px] font-semibold">Clique pour démarrer</span></>}
        {state === 'waiting' && <span className="text-white text-[16px] font-semibold">Attends le VERT…</span>}
        {state === 'ready' && <span className="text-black text-[20px] sm:text-[24px] font-bold uppercase tracking-wider">CLIQUE !</span>}
        {state === 'tooSoon' && <><span className="text-white text-[16px] font-bold mb-1">Trop tôt 🚦</span><span className="text-white/80 text-[13px]">Clique pour recommencer</span></>}
        {state === 'done' && (
          <>
            <span className="font-serif-editorial text-white text-[44px] sm:text-[60px] leading-none">{ms}<span className="text-[22px] text-white/50 ml-1">ms</span></span>
            <span className="text-white/60 text-[12.5px] mt-2 font-mono-spec">Clique pour recommencer</span>
          </>
        )}
      </button>

      {state === 'done' && ms && (
        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-[13px] sm:text-[14px]">
          <div className="rounded-xl bg-red-950/40 border border-red-700/30 p-4">
            <div className="text-red-300 text-[10.5px] uppercase tracking-wider font-bold mb-1">À 90 km/h</div>
            <div className="text-white font-serif-editorial text-[24px] sm:text-[30px] leading-none">{distance90} m</div>
            <div className="text-white/60 text-[12px] mt-1">parcourus avant de réagir</div>
          </div>
          <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4">
            <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-bold mb-1">À 50 km/h (en ville)</div>
            <div className="text-white font-serif-editorial text-[24px] sm:text-[30px] leading-none">{distance50} m</div>
            <div className="text-white/60 text-[12px] mt-1">une longueur de bus avant freinage</div>
          </div>
        </div>
      )}
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Temps de réaction moyen au volant : <strong className="text-white/70">1,0 à 1,5 s</strong>. Téléphone en main : ×3.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 5. ÉNERGIE CINÉTIQUE / DISTANCE D'ARRÊT
// ─────────────────────────────────────────────────────────────────
export function KineticEnergyDemo() {
  const [speed, setSpeed] = useState(50);
  // Distance arrêt = (v/10)² + (réaction 1s)*v/3.6
  const reaction = speed / 3.6; // m in 1s
  const braking = Math.pow(speed / 10, 2);
  const total = Math.round(reaction + braking);
  // Énergie cinétique relative (50 km/h = 1.0 ref)
  const energy = Math.pow(speed / 50, 2);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-kinetic">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Zap size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Vitesse × Énergie × Distance</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">L'énergie d'un choc augmente avec le <strong className="text-white">carré</strong> de la vitesse. Double la vitesse → quadruple l'énergie.</p>
        </div>
      </div>

      {/* Distance bar */}
      <div className="relative w-full bg-gradient-to-r from-emerald-950/30 via-orange-950/30 to-red-950/40 rounded-xl border border-white/10 overflow-hidden h-16 sm:h-20">
        <motion.div
          className="absolute left-0 top-0 bottom-0 bg-red-600/30 border-r-2 border-red-500"
          animate={{ width: `${Math.min(100, total / 1.5)}%` }}
          transition={{ duration: 0.4 }}
        />
        <div className="absolute inset-0 flex items-center px-4">
          <span className="font-serif-editorial text-white text-[26px] sm:text-[36px] leading-none">{total}<span className="text-[14px] sm:text-[16px] text-white/60 ml-1">m d'arrêt</span></span>
        </div>
      </div>

      {/* Energy comparison */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3 sm:p-4">
          <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-mono-spec">Énergie de choc</div>
          <div className="text-white font-serif-editorial text-[22px] sm:text-[28px] leading-none mt-1">× {energy.toFixed(1)}</div>
          <div className="text-white/55 text-[11.5px] mt-1">vs choc à 50 km/h</div>
        </div>
        <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3 sm:p-4">
          <div className="text-white/55 text-[10.5px] uppercase tracking-wider font-mono-spec">Équivalent chute</div>
          <div className="text-white font-serif-editorial text-[22px] sm:text-[28px] leading-none mt-1">{Math.round(Math.pow(speed / 3.6, 2) / (2 * 9.81))} m</div>
          <div className="text-white/55 text-[11.5px] mt-1">soit un étage = ~3 m</div>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Vitesse : {speed} km/h</label>
        <input
          data-testid="kinetic-speed-slider"
          type="range" min="30" max="130" step="5" value={speed}
          onChange={(e) => setSpeed(parseInt(e.target.value))}
          className="w-full mt-2 accent-red-500 h-2"
        />
        <div className="flex justify-between text-[11px] text-white/40 font-mono-spec mt-1">
          <span>30</span><span>50</span><span>70</span><span>90</span><span>110</span><span>130</span>
        </div>
      </div>
      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Formule officielle : distance d'arrêt = temps de réaction (1s) + (v/10)² sur sol sec.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 6. DISCRIMINATION PANNEAUX — flash & identify
// ─────────────────────────────────────────────────────────────────

// French signs as inline SVG (zero network dependency)
const SIGN_LIB = {
  stop: { label: 'STOP', cat: 'Intersection',
    svg: <svg viewBox="0 0 100 100"><polygon points="30,5 70,5 95,30 95,70 70,95 30,95 5,70 5,30" fill="#d93030" stroke="#fff" strokeWidth="4"/><text x="50" y="62" textAnchor="middle" fill="#fff" fontFamily="Arial Black" fontSize="22" fontWeight="900">STOP</text></svg> },
  cedez: { label: 'Cédez le passage', cat: 'Intersection',
    svg: <svg viewBox="0 0 100 100"><polygon points="6,14 94,14 50,92" fill="#fff" stroke="#d93030" strokeWidth="9" strokeLinejoin="round"/></svg> },
  priorite: { label: 'Route prioritaire', cat: 'Priorité',
    svg: <svg viewBox="0 0 100 100"><polygon points="50,5 95,50 50,95 5,50" fill="#fff" stroke="#000" strokeWidth="3"/><polygon points="50,15 85,50 50,85 15,50" fill="#ffd417"/></svg> },
  sens_interdit: { label: 'Sens interdit', cat: 'Interdiction',
    svg: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#d93030"/><rect x="20" y="42" width="60" height="16" fill="#fff"/></svg> },
  vitesse50: { label: 'Vitesse limitée 50', cat: 'Interdiction',
    svg: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#fff" stroke="#d93030" strokeWidth="10"/><text x="50" y="65" textAnchor="middle" fill="#000" fontFamily="Arial" fontSize="38" fontWeight="900">50</text></svg> },
  rond_point: { label: 'Carrefour à sens giratoire', cat: 'Obligation',
    svg: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#1c5fa6"/><g fill="#fff"><path d="M 50 20 A 30 30 0 0 1 76 65 L 68 60 A 22 22 0 0 0 50 28 Z"/><path d="M 76 65 A 30 30 0 0 1 24 65 L 32 60 A 22 22 0 0 0 68 60 Z"/><path d="M 24 65 A 30 30 0 0 1 50 20 L 50 28 A 22 22 0 0 0 32 60 Z"/></g><polygon points="72,70 82,68 76,78" fill="#fff"/><polygon points="28,70 24,80 18,68" fill="#fff"/><polygon points="50,15 44,25 56,25" fill="#fff"/></svg> },
  passage_pieton: { label: 'Passage piéton', cat: 'Indication',
    svg: <svg viewBox="0 0 100 100"><rect x="5" y="5" width="90" height="90" rx="4" fill="#1c5fa6" stroke="#fff" strokeWidth="3"/><polygon points="50,20 82,80 18,80" fill="#fff"/><g fill="#000"><circle cx="50" cy="42" r="4.5"/><path d="M 46 47 L 46 62 L 43 76 L 47 76 L 50 64 L 53 76 L 57 76 L 54 62 L 54 47 Z"/><path d="M 42 52 L 36 58 L 39 60 L 46 55 Z"/><path d="M 58 52 L 64 58 L 61 60 L 54 55 Z"/></g></svg> },
  travaux: { label: 'Travaux', cat: 'Danger',
    svg: <svg viewBox="0 0 100 100"><polygon points="50,8 92,86 8,86" fill="#fff" stroke="#d93030" strokeWidth="7" strokeLinejoin="round"/><g fill="#000"><circle cx="52" cy="42" r="4"/><rect x="49" y="46" width="6" height="16" rx="1"/><rect x="45" y="62" width="5" height="14" rx="1"/><rect x="55" y="62" width="5" height="14" rx="1"/><rect x="41" y="49" width="6" height="4" transform="rotate(-25 44 51)"/><rect x="55" y="47" width="6" height="4" transform="rotate(25 58 49)"/><rect x="60" y="43" width="3" height="26" transform="rotate(35 61.5 56)"/><polygon points="70,66 78,60 78,72" transform="rotate(35 74 66)"/></g></svg> },
};

const SIGN_KEYS = Object.keys(SIGN_LIB);

export function SignDiscriminationDemo() {
  const [phase, setPhase] = useState('idle'); // idle | flash | choose | result
  const [target, setTarget] = useState(null);
  const [choices, setChoices] = useState([]);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState({ ok: 0, ko: 0 });
  const [round, setRound] = useState(0);
  const totalRounds = 5;
  const flashRef = useRef(null);

  const newRound = useCallback(() => {
    const t = SIGN_KEYS[Math.floor(Math.random() * SIGN_KEYS.length)];
    const others = SIGN_KEYS.filter(k => k !== t).sort(() => Math.random() - 0.5).slice(0, 3);
    const opts = [t, ...others].sort(() => Math.random() - 0.5);
    setTarget(t);
    setChoices(opts);
    setPicked(null);
    setPhase('flash');
    flashRef.current = setTimeout(() => setPhase('choose'), 600);
  }, []);

  const pick = (key) => {
    if (phase !== 'choose') return;
    setPicked(key);
    setScore((s) => key === target ? { ...s, ok: s.ok + 1 } : { ...s, ko: s.ko + 1 });
    setPhase('result');
  };

  const next = () => {
    if (round + 1 >= totalRounds) {
      setPhase('summary');
    } else {
      setRound((r) => r + 1);
      newRound();
    }
  };

  const reset = () => {
    setRound(0); setScore({ ok: 0, ko: 0 });
    newRound();
  };

  useEffect(() => () => clearTimeout(flashRef.current), []);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-signs-discrimination">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Octagon size={18} /></div>
        <div className="flex-1">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Discrimination de panneaux</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Un panneau apparaît <strong className="text-white">600 ms</strong> (comme en conduite réelle). Identifie-le parmi 4. <span className="text-white/45">5 manches.</span></p>
        </div>
        <div className="text-right text-white/60 text-[11.5px] font-mono-spec shrink-0">
          {phase !== 'idle' && phase !== 'summary' && <>Manche {round + 1} / {totalRounds}</>}
        </div>
      </div>

      {/* Stage */}
      <div className="relative w-full bg-gradient-to-b from-[#1a1a1c] to-[#0a0a0b] rounded-xl border border-white/5 flex items-center justify-center" style={{ aspectRatio: '5 / 3' }}>
        {phase === 'idle' && (
          <button onClick={() => { setRound(0); setScore({ ok: 0, ko: 0 }); newRound(); }} data-testid="signs-start" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold inline-flex items-center gap-2">
            <Play size={14} /> Commencer
          </button>
        )}
        {phase === 'flash' && target && (
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-28 h-28 sm:w-36 sm:h-36">
            {SIGN_LIB[target].svg}
          </motion.div>
        )}
        {phase === 'choose' && (
          <div className="text-center">
            <div className="text-white text-[14.5px] sm:text-[16px] font-semibold mb-1">Quel panneau ?</div>
            <div className="text-white/50 text-[12px]">Choisis ci-dessous ↓</div>
          </div>
        )}
        {phase === 'result' && target && (
          <div className="text-center">
            <div className={`text-[15px] sm:text-[18px] font-bold mb-2 ${picked === target ? 'text-emerald-400' : 'text-red-400'}`}>
              {picked === target ? '✓ Bonne réponse !' : '✗ Raté.'}
            </div>
            <div className="text-white/60 text-[12.5px] sm:text-[13px]">C'était : <strong className="text-white">{SIGN_LIB[target].label}</strong></div>
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mt-3">{SIGN_LIB[target].svg}</div>
          </div>
        )}
        {phase === 'summary' && (
          <div className="text-center px-4">
            <div className="font-serif-editorial text-white text-[28px] sm:text-[36px] leading-none">{score.ok}<span className="text-white/40 text-[18px]"> / {totalRounds}</span></div>
            <div className="text-white/60 text-[12.5px] mt-2">
              {score.ok === totalRounds && '🏆 Parfait — tu lis la route comme un pro.'}
              {score.ok >= 3 && score.ok < totalRounds && '👍 Bien. Continue à travailler les panneaux complexes.'}
              {score.ok < 3 && '⚠ À retravailler — relis le dossier ETG « Panneaux ».'}
            </div>
            <button onClick={reset} data-testid="signs-restart" className="mt-4 h-10 px-4 rounded-full border border-white/15 text-white text-[12.5px] hover:bg-white/5 inline-flex items-center gap-2"><RotateCcw size={13} /> Rejouer</button>
          </div>
        )}
      </div>

      {/* Choices */}
      {(phase === 'choose' || phase === 'result') && (
        <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-3">
          {choices.map((k) => {
            const isPicked = picked === k;
            const isCorrect = k === target;
            const showResult = phase === 'result';
            return (
              <button
                key={k}
                onClick={() => pick(k)}
                data-testid={`sign-choice-${k}`}
                disabled={phase !== 'choose'}
                className={`relative aspect-square rounded-xl border p-2 sm:p-3 transition ${
                  showResult && isCorrect ? 'border-emerald-500 bg-emerald-950/30' :
                  showResult && isPicked ? 'border-red-500 bg-red-950/30' :
                  'border-white/10 bg-[#141416] hover:border-red-700/40'
                }`}
              >
                {SIGN_LIB[k].svg}
              </button>
            );
          })}
        </div>
      )}

      {phase === 'result' && (
        <button onClick={next} data-testid="signs-next" className="mt-4 w-full h-10 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold">
          {round + 1 >= totalRounds ? 'Voir le résultat' : 'Manche suivante'}
        </button>
      )}

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">Au volant à 90 km/h, tu as ~1 s pour identifier un panneau à 25 m. C'est tout.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 7. SIMULATEUR FATIGUE — PVT (Psychomotor Vigilance Task)
// ─────────────────────────────────────────────────────────────────
export function FatigueDemo() {
  const TEST_DURATION = 30; // seconds
  const [phase, setPhase] = useState('idle'); // idle | running | done
  const [stim, setStim] = useState(false);
  const [reactions, setReactions] = useState([]); // ms array
  const [lapses, setLapses] = useState(0); // RT > 500ms
  const [timeLeft, setTimeLeft] = useState(TEST_DURATION);
  const startRef = useRef(0);
  const stimStartRef = useRef(0);
  const tickRef = useRef(null);
  const stimRef = useRef(null);

  const scheduleStim = useCallback(() => {
    const delay = 2000 + Math.random() * 4000; // 2–6s between stimuli
    stimRef.current = setTimeout(() => {
      stimStartRef.current = performance.now();
      setStim(true);
    }, delay);
  }, []);

  const start = () => {
    setPhase('running');
    setReactions([]); setLapses(0);
    setTimeLeft(TEST_DURATION);
    startRef.current = performance.now();
    tickRef.current = setInterval(() => {
      const elapsed = (performance.now() - startRef.current) / 1000;
      const left = Math.max(0, TEST_DURATION - elapsed);
      setTimeLeft(left);
      if (left <= 0) {
        clearInterval(tickRef.current);
        clearTimeout(stimRef.current);
        setStim(false);
        setPhase('done');
      }
    }, 100);
    scheduleStim();
  };

  const onClick = () => {
    if (phase !== 'running') return;
    if (stim) {
      const rt = Math.round(performance.now() - stimStartRef.current);
      setReactions((r) => [...r, rt]);
      if (rt > 500) setLapses((l) => l + 1);
      setStim(false);
      scheduleStim();
    } else {
      // false start - penalty 1 lapse
      setLapses((l) => l + 1);
    }
  };

  useEffect(() => () => { clearInterval(tickRef.current); clearTimeout(stimRef.current); }, []);

  const avgRT = reactions.length ? Math.round(reactions.reduce((a, b) => a + b, 0) / reactions.length) : 0;
  const status = avgRT === 0 ? 'idle' : avgRT < 300 ? 'fresh' : avgRT < 450 ? 'normal' : avgRT < 600 ? 'tired' : 'critical';
  const STATUS_LABEL = {
    fresh: { c: 'text-emerald-400', t: '🟢 Frais & dispo', d: 'Réflexes au top, conduite sereine.' },
    normal: { c: 'text-yellow-400', t: '🟡 Conduite normale', d: 'Vigilance ok, fais une pause toutes les 2h.' },
    tired: { c: 'text-orange-400', t: '🟠 Fatigue installée', d: 'Équivalent à 0,5 g/L d\'alcool — fais une pause MAINTENANT.' },
    critical: { c: 'text-red-400', t: '🔴 Danger', d: 'Tu ne devrais pas être au volant. Arrête-toi.' },
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-fatigue">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Coffee size={18} /></div>
        <div className="flex-1">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Test de vigilance (PVT)</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Pendant 30 secondes, clique <strong className="text-white">dès que la zone devient rouge</strong>. Ce test est utilisé par la NASA et l'armée pour mesurer la fatigue.</p>
        </div>
        {phase === 'running' && (
          <div className="text-right text-white font-mono-spec shrink-0">
            <div className="text-[20px] sm:text-[24px] font-bold">{Math.ceil(timeLeft)}<span className="text-[12px] text-white/50">s</span></div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={phase === 'idle' ? start : phase === 'done' ? start : onClick}
        data-testid="fatigue-zone"
        className={`relative w-full rounded-xl flex items-center justify-center select-none transition-colors border border-white/10 ${
          stim ? 'bg-red-600' : phase === 'running' ? 'bg-[#080809]' : 'bg-[#141416] hover:bg-[#1a1a1c]'
        }`}
        style={{ aspectRatio: '16 / 7' }}
      >
        {phase === 'idle' && <><Play size={32} className="text-white/60 mr-2" /><span className="text-white text-[14.5px] font-semibold">Commencer le test (30 s)</span></>}
        {phase === 'running' && !stim && <span className="text-white/40 text-[13px] font-mono-spec">Attends le signal rouge…</span>}
        {phase === 'running' && stim && <span className="text-white text-[22px] sm:text-[28px] font-black uppercase tracking-wider">CLIQUE !</span>}
        {phase === 'done' && (
          <div className="text-center px-4 py-3">
            <div className="font-serif-editorial text-white text-[36px] sm:text-[48px] leading-none">{avgRT}<span className="text-[18px] text-white/50 ml-1">ms</span></div>
            <div className="text-white/60 text-[12px] mt-1">temps de réaction moyen · {reactions.length} stimuli · {lapses} lapses</div>
            <div className={`mt-3 text-[14px] font-semibold ${STATUS_LABEL[status]?.c || ''}`}>{STATUS_LABEL[status]?.t}</div>
            <div className="text-white/65 text-[12.5px] mt-1">{STATUS_LABEL[status]?.d}</div>
            <div className="text-white/45 text-[11.5px] mt-2">Clique pour recommencer.</div>
          </div>
        )}
      </button>

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">17 heures d'éveil = 0,5 g/L. 24 heures = 1 g/L. La fatigue tue autant que l'alcool.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 8. VISION FLOUE ALCOOL
// ─────────────────────────────────────────────────────────────────
export function AlcoholVisionDemo() {
  const [bac, setBac] = useState(0); // g/L

  // Effects scale with BAC
  const blur = Math.min(8, bac * 10);          // 0 → 8px
  const doubleShift = Math.min(8, bac * 11);   // 0 → 8px
  const dim = Math.max(0.55, 1 - bac * 0.4);   // 1 → 0.55
  const tunnel = Math.max(28, 60 - bac * 35);  // 60% → 28% radius

  const legalStatus =
    bac === 0 ? { c: 'emerald', t: '🟢 0,0 g/L', d: 'Conduite légale, vision normale.' } :
    bac < 0.2 ? { c: 'emerald', t: 'Sous le seuil', d: 'Conduite légale, mais déjà perte d\'acuité.' } :
    bac < 0.5 ? { c: 'yellow', t: '⚠ Seuil jeune permis (0,2 g/L)', d: 'Permis probatoire : retrait 6 points + 135 € + 3 ans suspension.' } :
    bac < 0.8 ? { c: 'orange', t: '⚠ Contravention (0,5 g/L)', d: '6 points + 135 € + suspension. Vision binoculaire altérée.' } :
    { c: 'red', t: '🚨 Délit (0,8 g/L+)', d: '8 ans de prison max, 9 000 € d\'amende, annulation du permis.' };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="demo-alcohol-vision">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0"><Wine size={18} /></div>
        <div>
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight">Ta vision sous alcool</h3>
          <p className="text-white/60 text-[13px] sm:text-[14px] mt-1">Augmente le taux d'alcool : la scène se floute, dédouble, perd en luminosité — comme ton cerveau au volant.</p>
        </div>
      </div>

      {/* Stage — simulated driving scene */}
      <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="alcohol-scene">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(180deg, #2a3552 0%, #1c2640 30%, #0a0a0b 60%, #0a0a0b 100%)',
            filter: `blur(${blur}px) brightness(${dim})`,
          }}
        />
        {/* Road perspective */}
        <div className="absolute inset-0" style={{ filter: `blur(${blur}px) brightness(${dim})` }}>
          {/* Road */}
          <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-[60%] h-[55%]" style={{ background: 'linear-gradient(to top, #2a2a2c 0%, #1a1a1c 70%, transparent 100%)', clipPath: 'polygon(20% 100%, 80% 100%, 60% 0, 40% 0)' }} />
          {/* Center line dashes */}
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="absolute left-1/2 -translate-x-1/2 bg-yellow-300/80" style={{ width: `${3 - i * 0.5}px`, height: `${8 - i}px`, bottom: `${10 + i * 12}%`, opacity: 1 - i * 0.15 }} />
          ))}
          {/* Front car taillights */}
          <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: '38%' }}>
            <div className="flex gap-3">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" style={{ boxShadow: '0 0 12px rgba(239,68,68,0.9)' }} />
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" style={{ boxShadow: '0 0 12px rgba(239,68,68,0.9)' }} />
            </div>
          </div>
          {/* Street sign on right */}
          <div className="absolute right-[10%] top-[25%] w-12 h-12 sm:w-16 sm:h-16">
            <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="#fff" stroke="#d93030" strokeWidth="10"/><text x="50" y="65" textAnchor="middle" fill="#000" fontFamily="Arial" fontSize="38" fontWeight="900">50</text></svg>
          </div>
        </div>
        {/* Double vision overlay */}
        {bac > 0.1 && (
          <div
            className="absolute inset-0 pointer-events-none mix-blend-screen"
            style={{
              backgroundImage: 'linear-gradient(180deg, #2a3552 0%, #1c2640 30%, #0a0a0b 60%, #0a0a0b 100%)',
              filter: `blur(${blur}px) brightness(${dim})`,
              transform: `translateX(${doubleShift}px)`,
              opacity: Math.min(0.5, bac * 0.7),
            }}
          />
        )}
        {/* Tunnel vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse ${tunnel}% ${tunnel * 0.7}% at 50% 50%, transparent 0%, transparent 50%, rgba(0,0,0,0.9) 100%)`,
          }}
        />
        {/* HUD */}
        <div className="absolute top-3 left-3 px-3 py-1.5 rounded-md bg-black/70 backdrop-blur border border-white/15 text-white font-mono-spec text-[12px] sm:text-[13px]">
          BAC : <span className="text-red-400 font-bold">{bac.toFixed(2)} g/L</span>
        </div>
      </div>

      {/* Slider */}
      <div className="mt-5">
        <label className="text-white/70 text-[12.5px] sm:text-[13px] font-mono-spec uppercase tracking-wider">Alcoolémie (g/L de sang)</label>
        <input
          data-testid="alcohol-slider"
          type="range" min="0" max="1.2" step="0.05" value={bac}
          onChange={(e) => setBac(parseFloat(e.target.value))}
          className="w-full mt-2 accent-red-500 h-2"
        />
        <div className="flex justify-between text-[11px] text-white/40 font-mono-spec mt-1">
          <span>0,0</span><span>0,2</span><span>0,5</span><span>0,8</span><span>1,0</span><span>1,2</span>
        </div>
      </div>

      <div className={`mt-4 rounded-xl p-3 sm:p-4 text-[13px] sm:text-[13.5px] border transition-colors ${
        legalStatus.c === 'red' ? 'bg-red-950/40 border-red-700/50 text-red-200' :
        legalStatus.c === 'orange' ? 'bg-orange-950/40 border-orange-700/50 text-orange-200' :
        legalStatus.c === 'yellow' ? 'bg-yellow-950/30 border-yellow-700/40 text-yellow-200' :
        'bg-emerald-950/30 border-emerald-700/30 text-emerald-200'
      }`}>
        <div className="font-bold mb-0.5">{legalStatus.t}</div>
        <div className="text-[12.5px] opacity-90">{legalStatus.d}</div>
      </div>

      <p className="mt-4 text-[11.5px] text-white/45 font-mono-spec">À 0,5 g/L : temps de réaction × 2, vision périphérique réduite de 30 %. Article L234-1 du Code de la route.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// REGISTRY — point unique pour embed & hub
// ─────────────────────────────────────────────────────────────────
export const DEMOS = {
  mariotte: {
    component: MariotteDemo,
    title: 'Tache aveugle de Mariotte',
    short: 'La croix qui disparaît quand on ferme un œil.',
    cat: 'Vision',
    color: 'red',
    icon: Eye,
  },
  'vehicle-blindspot': {
    component: VehicleBlindSpotDemo,
    title: 'Angles morts du véhicule',
    short: 'Déplace le cycliste autour de la voiture pour repérer les zones invisibles.',
    cat: 'Vision',
    color: 'red',
    icon: Car,
  },
  'visual-tunnel': {
    component: VisualTunnelDemo,
    title: 'L\'effet tunnel selon la vitesse',
    short: 'Slider 30→130 km/h : ton champ visuel se rétrécit à mesure que tu accélères.',
    cat: 'Vitesse',
    color: 'red',
    icon: Gauge,
  },
  'reaction-time': {
    component: ReactionTimeDemo,
    title: 'Test de temps de réaction',
    short: 'Clique au signal vert. Découvre combien de mètres tu parcours à 90 km/h avant de réagir.',
    cat: 'Réaction',
    color: 'red',
    icon: Timer,
  },
  kinetic: {
    component: KineticEnergyDemo,
    title: 'Énergie cinétique × distance d\'arrêt',
    short: 'Tu doubles la vitesse ? L\'énergie quadruple. La distance d\'arrêt aussi.',
    cat: 'Vitesse',
    color: 'red',
    icon: Zap,
  },
  'signs-discrimination': {
    component: SignDiscriminationDemo,
    title: 'Discrimination de panneaux',
    short: 'Flash 600 ms : identifie le panneau parmi 4. 5 manches.',
    cat: 'Panneaux',
    color: 'red',
    icon: Octagon,
  },
  fatigue: {
    component: FatigueDemo,
    title: 'Test de vigilance (PVT)',
    short: 'Mesure ton temps de réaction sur 30 s. Détecte ta fatigue comme la NASA.',
    cat: 'Réaction',
    color: 'red',
    icon: Coffee,
  },
  'alcohol-vision': {
    component: AlcoholVisionDemo,
    title: 'Ta vision sous alcool',
    short: 'Slider de 0 à 1,2 g/L : la scène se floute, dédouble, s\'assombrit.',
    cat: 'Alcool',
    color: 'red',
    icon: Wine,
  },
};

// Wrapper for inline article embed
export function DemoEmbed({ slug }) {
  const demo = DEMOS[slug];
  if (!demo) return null;
  const Component = demo.component;
  return (
    <div className="my-8" data-testid={`demo-embed-${slug}`}>
      <Component />
    </div>
  );
}
